import { faNum } from "../game/cards";
import { fa } from "./fa";
import { PICTURES } from "./pictures";
import { playable } from "./shared";
import { HANDMADE } from "./stages";
import { Thumb } from "./Thumb";

/**
 * اتاقِ دوستانِ فلش‌ها: میزبان نقش و اندازه را برمی‌گزیند، دوستان می‌آیند و مرحله را با هم شروع می‌کنند (کاربر، ۷ آبان:
 * «در حالتِ آنلاین انتخاب کند، افراد اضافه شوند و مرحله را شروع کنند»). همان قانون‌های match.GameRules ِ سرور:
 * pic ۰ قرعه، ۱ تا ۲۴ نقشِ دست‌ساز (به ترتیبِ HANDMADE)؛ size ۰ میانه، ۱ کوچک، ۲ بزرگ؛ teams ۲ به ۲ با چهار نفر
 * (روبه‌روها یارند و تمام‌کرده به یارش کمک می‌کند). نقشِ دوبرشی با سه یا چهار نفر جا نمی‌شود و سرور سرِ شروع قرعه
 * می‌کشد؛ برچسبِ «۲ نفره» همین را از پیش می‌گوید. تیمی هر نقشی را می‌پذیرد (هر تیم یک نیمه)، پس آن‌جا برچسب نیست.
 *
 * میز چند دور است (کاربر، ۷ آبان: «وقتی میز باز می‌کنی پشتِ سرِ هم نقشه‌های مختلف بازی می‌کنی»): rounds ۰ پنج، ۱ سه،
 * ۲ ده. mode ۱ همکاری است — همه یک تیم روی نقشه‌ی بزرگِ تکه‌تکه (coop.ts)؛ نقش و تیمی آن‌جا معنا ندارند.
 */
export interface ArrowsRoom { pic: number; size: number; teams: number; mode: number; rounds: number }

const KEY = "bazivan.arrows.room";
const SIZES: [number, string][] = [[1, "کوچک"], [0, "میانه"], [2, "بزرگ"]];
/** [قانونِ rounds، شمارِ دورها]، به ترتیبِ نمایش. */
const ROUNDS: [number, number][] = [[1, 3], [0, 5], [2, 10]];
export const roundsOf = (rule: number): number => ROUNDS.find(([v]) => v === rule)?.[1] ?? 5;

export function loadArrowsRoom(): ArrowsRoom {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<ArrowsRoom> | null;
    const pic = Number(v?.pic), size = Number(v?.size);
    const rounds = Number(v?.rounds);
    return {
      pic: pic >= 0 && pic <= HANDMADE.length ? pic : 0, size: size >= 0 && size <= 2 ? size : 0, teams: v?.teams === 1 ? 1 : 0,
      mode: v?.mode === 1 ? 1 : 0, rounds: rounds >= 0 && rounds <= 2 ? rounds : 0,
    };
  } catch {
    return { pic: 0, size: 0, teams: 0, mode: 0, rounds: 0 };
  }
}

export function saveArrowsRoom(r: ArrowsRoom): void {
  try { localStorage.setItem(KEY, JSON.stringify(r)); } catch { /* بی‌حافظه هم کار می‌کند */ }
}

export const arrowsRoomRules = (r: ArrowsRoom): Record<string, number> =>
  r.mode === 1 ? { mode: 1, size: r.size, rounds: r.rounds } : { pic: r.pic, size: r.size, teams: r.teams, rounds: r.rounds };

export function arrowsRoomSummary(r: ArrowsRoom): string {
  const size = SIZES.find(([v]) => v === r.size)?.[1] ?? "میانه";
  const n = fa(roundsOf(r.rounds));
  if (r.mode === 1) return `همکاری، ${size}، ${n} نقشه`;
  return `${r.pic ? HANDMADE[r.pic - 1]![1] : "نقش قرعه"}، ${size}${r.teams ? "، تیمی" : ""}، ${n} دور`;
}

/** قانون‌های اتاق (room.rules) ← ArrowsRoom، برای قرصِ لابی. */
export const arrowsRoomFrom = (rules: Record<string, number | undefined>): ArrowsRoom => ({
  pic: rules["pic"] ?? 0, size: rules["size"] ?? 0, teams: rules["teams"] ?? 0, mode: rules["mode"] ?? 0, rounds: rules["rounds"] ?? 0,
});

export function ArrowsRoomPicker({ room, set }: { room: ArrowsRoom; set: (r: ArrowsRoom) => void }) {
  const coop = room.mode === 1;
  return (
    <div className="stack ar-roompick" style={{ gap: 8 }}>
      <div className="ar-seg ar-modeseg" role="radiogroup" aria-label="حالت">
        {([[0, "مسابقه"], [1, "همکاری"]] as const).map(([v, label]) => (
          <button key={v} type="button" role="radio" aria-checked={room.mode === v} className={room.mode === v ? "on" : ""}
            onClick={() => set({ ...room, mode: v })}>{label}</button>
        ))}
      </div>
      {coop && <div className="muted">همه با هم یک تیم؛ هر تکه که تمام شود تکه‌های کنارش باز می‌شوند.</div>}
      <b className="morelabel">{coop ? "نقشه‌ها" : "دورها"}</b>
      <div className="row" style={{ gap: 8 }} role="radiogroup" aria-label={coop ? "نقشه‌ها" : "دورها"}>
        {ROUNDS.map(([v, n]) => (
          <button key={v} type="button" role="radio" aria-checked={room.rounds === v} className={"chip" + (room.rounds === v ? " on" : "")}
            onClick={() => set({ ...room, rounds: v })}>{fa(n)}</button>
        ))}
      </div>
      {!coop && <>
      <b className="morelabel">نقش</b>
      <div className="ar-picgrid" role="radiogroup" aria-label="نقش">
        <button type="button" role="radio" aria-checked={room.pic === 0} className={"ar-pictile" + (room.pic === 0 ? " on" : "")}
          onClick={() => set({ ...room, pic: 0 })} aria-label="قرعه">
          <svg viewBox="0 0 24 24" className="ar-dice" aria-hidden>
            <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
            <circle cx="8.5" cy="8.5" r="1.5" /><circle cx="15.5" cy="15.5" r="1.5" /><circle cx="12" cy="12" r="1.5" />
          </svg>
          <small>قرعه</small>
        </button>
        {HANDMADE.map(([id, name], i) => (
          <button key={id} type="button" role="radio" aria-checked={room.pic === i + 1} aria-label={name}
            className={"ar-pictile" + (room.pic === i + 1 ? " on" : "")} onClick={() => set({ ...room, pic: i + 1 })}>
            <Thumb pic={id} />
            <small>{name}</small>
            {!room.teams && !playable(PICTURES[id], 4) && <i className="ar-two">{faNum(2)} نفره</i>}
          </button>
        ))}
      </div>
      </>}
      <b className="morelabel">اندازه</b>
      <div className="row" style={{ gap: 8 }} role="radiogroup" aria-label="اندازه">
        {SIZES.map(([v, fa]) => (
          <button key={v} type="button" role="radio" aria-checked={room.size === v} className={"chip" + (room.size === v ? " on" : "")}
            onClick={() => set({ ...room, size: v })}>{fa}</button>
        ))}
        {!coop && (
          <button type="button" aria-pressed={room.teams === 1} className={"chip" + (room.teams ? " on" : "")}
            onClick={() => set({ ...room, teams: room.teams ? 0 : 1 })}>تیمی</button>
        )}
      </div>
      {!coop && room.teams === 1 && <div className="muted">با چهار نفر دو به دو؛ روبه‌روها یارند.</div>}
    </div>
  );
}
